<template>
    <div>
        <div class="mt-4 valign-middle-children bank-reconciliation-filter">
            <div v-if="error" class="alert alert-danger formAlert">
                {{ error }}
            </div>

            <div class="bank-reconciliation-filter__fields">
                <el-select
                    v-model="doctype"
                    :placeholder="$t.__('bankreconciliation.Filter.document_type')"
                    :popperOptions="{
                        strategy: 'fixed',
                    }"
                >
                    <el-option
                        v-for="doctype in doctypes"
                        :key="doctype.value"
                        :label="doctype.label"
                        :value="doctype.value"
                    >
                    </el-option>
                </el-select>

                <div class="form-input">
                    <el-input
                        v-model="thirdname"
                        :disabled="doctype == ''"
                        :placeholder="thirdType"
                        clearable
                        @change="searchDocs"
                    />
                </div>

                <el-date-picker
                    v-bind="pickerOptions"
                    v-model="docDates"
                    :disabled="doctype == ''"
                    :clearable="false"
                    type="daterange"
                    :format="datePickerFormat"
                    valueFormat="YYYY-MM-DD"
                    :rangeSeparator="$t.__('bankreconciliation.Filter.to')"
                    :startPlaceholder="$t.__('common.labels.start_date')"
                    :endPlaceholder="$t.__('common.labels.end_date')"
                    :popperOptions="{
                        strategy: 'fixed',
                    }"
                >
                </el-date-picker>

                <input-number
                    v-model="amount"
                    :disabled="doctype == ''"
                    :placeholder="$t.__('common.labels.amount')"
                    :emptyAtStart="true"
                    :min="0"
                    :precision="numberFormat.precision"
                    :separatorDecimal="numberFormat.decimal"
                    :separatorThousand="numberFormat.thousand"
                />

                <div class="form-input">
                    <el-input
                        v-model="docident"
                        :disabled="doctype == ''"
                        clearable
                        :placeholder="$t.__('bankreconciliation.Filter.document_number')"
                        @change="searchDocs"
                    />
                </div>

                <CompassButton
                    :label="$t.__('common.actions.search')"
                    type="primary"
                    :disabled="doctype == ''"
                    @click="searchDocs"
                />
            </div>
        </div>

        <el-checkbox v-model="reconciled" class="mt-2">
            {{ $l10n.__('Documents rapprochés') }}
        </el-checkbox>

        <paginator
            v-if="docs.length > 0"
            class="flex-right"
            :pagination="pagination"
            @pagination:update="goToPage"
        />
    </div>
</template>

<script>
import datePickerShortCut from '../../mixins/datePickerOptionsRB.js';
import Paginator from '../utilities/Paginator.vue';
import InputNumber from 'vcomponents/utilities/InputNumber.vue';
import { createNamespacedHelpers } from 'vuex';

const { mapGetters } = createNamespacedHelpers('corp');
const { mapGetters: reconciliationGetters, mapMutations: reconciliationMutations } =
    createNamespacedHelpers('reconciliationStore');

export default {
    components: {
        'input-number': InputNumber,
        Paginator,
    },
    mixins: [datePickerShortCut],
    props: {
        transactionDate: {
            type: Date,
            required: true,
        },
    },
    data() {
        return {
            error: false,

            doctypes: [
                {
                    value: 'invoice',
                    label: this.$l10n.__('Facture client'),
                },
                {
                    value: 'creditnote',
                    label: this.$l10n.__('Avoir client'),
                },
                {
                    value: 'purInvoice',
                    label: this.$l10n.__('Facture fournisseur'),
                },
                {
                    value: 'purCreditNote',
                    label: this.$l10n.__('Avoir fournisseur'),
                },
            ],

            numberFormat: {
                precision: parseInt(App.numformat.precision),
                decimal: App.numformat.decimals,
                thousand: App.numformat.thousands,
            },

            doctype: '',
            thirdname: '',
            docDates: this.generateDefaultRange(this.transactionDate),
            amount: 0,
            docident: '',
            reconciled: false,
        };
    },
    computed: {
        ...reconciliationGetters(['docs', 'pagination', 'transactionId']),
        thirdType: function () {
            const doctypeForClient = ['creditNote', 'invoice'];
            const doctypeForSupplier = ['purInvoice', 'purCreditNote'];
            if (doctypeForClient.indexOf(this.doctype) !== -1) {
                return this.$l10n.__('Clients');
            } else if (doctypeForSupplier.indexOf(this.doctype) !== -1) {
                return this.$l10n.__('Fournisseurs');
            } else {
                return this.$l10n.__('Clients / Fournisseurs');
            }
        },

        ...mapGetters(['datePickerFormat']),
    },
    methods: {
        ...reconciliationMutations([
            'setDocs',
            'setDoctype',
            'setPagination',
            'docsLoading',
            'docsLoaded',
        ]),
        // Form submit
        searchDocs() {
            this.docsLoading();
            this.error = false;

            const options = {
                reconciliation_doctype: this.doctype,
                reconciliation_startDate: this.docDates[0],
                reconciliation_endDate: this.docDates[1],
                reconciliation_amount: this.amount,
                reconciliation_ident: this.docident,
                reconciliation_third: this.thirdname,
                reconciliation_pagination: this.pagination,
                reconciliation_transactionId: this.transactionId,
                reconciliation_reconciled: this.reconciled,
            };

            $.post(
                './?_f=bankreconciliation_reconciliation&action=getDocs',
                options,
                (data) => {
                    if (data.form.status == 'failed') {
                        if (data.form.msg != '') {
                            this.error = data.form.msg;
                        }
                        this.docsLoaded();
                        return;
                    }

                    const docs = data.data.map((doc) => {
                        return {
                            link: doc.formatted_ident.match(/href="(.*)"/)[1],
                            step: doc.step,
                            docid: doc.id,
                            ident: doc.ident,
                            thirdname: doc.thirdname,
                            dueAmount: doc.dueAmount,
                            totalAmount: doc.totalAmount,
                            formattedTotalAmount: doc.formatted_totalAmount,
                            hasDeadlines: doc.hasDeadlines == 'Y',
                            displayedDate: doc.displayedDate,
                            displayedStep: doc.formatted_step,
                        };
                    });

                    this.setDocs(docs);
                    this.setDoctype(this.doctype);
                    this.setPagination(data.meta.pagination);
                    this.docsLoaded();
                },
                'json',
            );
        },

        // Helper
        generateDefaultRange: function (date) {
            const start = moment(date).subtract(354, 'days');
            const end = moment(date).add(10, 'days');
            return [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')];
        },

        // Event
        goToPage(page) {
            const pagination = this.pagination;
            pagination.current_page = page;
            this.setPagination(pagination);

            this.searchDocs();
        },
    },
};
</script>

<style scoped lang="scss">
.bank-reconciliation-filter {
    :deep(.el-select) {
        width: 200px;
    }
    // Reset CSS to element.io
    .el-input__prefix,
    .el-input__suffix {
        top: -5px;
    }
    :deep(.el-date-editor.el-date-editor--daterange) {
        width: 150px;
    }
    :deep(.el-date-editor .el-input__inner),
    :deep(.el-date-editor.el-input__inner) {
        width: 350px;
        padding: 0 30px;
    }
    &__fields {
        display: flex;
        gap: var(--spacing-1);
    }
}
</style>
